import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  convertToPixelCrop,
  makeAspectCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './CropTool.css';
import { toolApplyClass, toolCancelClass, toolChipClass } from './editorUi';

interface CropToolProps {
  image: string;
  onApply: (newImage: string) => void;
  onClose: () => void;
}

export function CropTool({ image, onApply, onClose }: CropToolProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;

    // Cria um crop inicial no centro da imagem
    const nextCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 80,
        },
        16 / 9,
        width,
        height
      ),
      width,
      height
    );

    setCrop(nextCrop);
    // onComplete nem sempre dispara (ex.: imagem em cache); sem pixel crop o Aplicar não faz nada
    setCompletedCrop(convertToPixelCrop(nextCrop, width, height));
  }, []);

  /**
   * Extrai o retângulo do crop em pixels naturais e desenha no canvas.
   * Não usa devicePixelRatio no tamanho do canvas — isso multiplicava área (ex.: ×4) e
   * estourava o limite (~67M px), gerando "Canvas area exceeds..." e toBlob null.
   */
  const getCroppedImg = (
    image: HTMLImageElement,
    crop: PixelCrop,
    fileName: string = 'cropped-image.jpg'
  ): Promise<File> => {
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const sx = crop.x * scaleX;
    const sy = crop.y * scaleY;
    const sw = crop.width * scaleX;
    const sh = crop.height * scaleY;

    let dw = Math.max(1, Math.floor(sw));
    let dh = Math.max(1, Math.floor(sh));

    // Limite seguro de área (evita aviso / falha de toBlob em Safari e Chrome)
    const MAX_CANVAS_AREA = 67108864;
    if (dw * dh > MAX_CANVAS_AREA) {
      const factor = Math.sqrt((dw * dh) / MAX_CANVAS_AREA);
      dw = Math.max(1, Math.floor(dw / factor));
      dh = Math.max(1, Math.floor(dh / factor));
    }

    const canvas = document.createElement('canvas');
    canvas.width = dw;
    canvas.height = dh;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, dw, dh);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          const file = new File([blob], fileName, { type: 'image/jpeg' });
          resolve(file);
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const handleApply = async () => {
    if (!imgRef.current) return;

    const el = imgRef.current;
    let pixelCrop: PixelCrop | undefined = completedCrop;
    if (!pixelCrop && crop) {
      pixelCrop = convertToPixelCrop(crop, el.width, el.height);
    }
    if (!pixelCrop) {
      console.warn('[CropTool] Sem área de corte definida');
      return;
    }

    try {
      const croppedImageFile = await getCroppedImg(el, pixelCrop);
      
      // Converte o arquivo para base64
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        onApply(base64);
        onClose();
      };
      reader.readAsDataURL(croppedImageFile);
    } catch (error) {
      console.error('Erro ao cortar imagem:', error);
    }
  };

  // Previne zoom da página no iPhone quando o crop está ativo (restaura ao sair)
  useEffect(() => {
    const metaViewport = document.querySelector('meta[name="viewport"]');
    const originalContent = metaViewport?.getAttribute('content') ?? '';
    if (metaViewport) {
      metaViewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
    return () => {
      if (metaViewport && originalContent) {
        metaViewport.setAttribute('content', originalContent);
      }
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      <div className="w-full">
        <div className="flex-1 flex items-center justify-center overflow-hidden">
          <div className="flex flex-col items-center gap-4 crop-tool-mobile-wrapper max-w-full">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c: PixelCrop) => setCompletedCrop(c)}
              aspect={undefined} // Permite qualquer proporção
              minWidth={50}
              minHeight={50}
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={image}
                onLoad={onImageLoad}
                className="max-w-full !max-h-[60vh] !object-contain select-none"
                draggable={false}
              />
            </ReactCrop>
            
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCrop(undefined)}
                className={toolChipClass}
              >
                <span>Limpar</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (imgRef.current) {
                    const { width, height } = imgRef.current;
                    const newCrop = centerCrop(
                      makeAspectCrop(
                        {
                          unit: '%',
                          width: 80,
                        },
                        16 / 9,
                        width,
                        height
                      ),
                      width,
                      height
                    );
                    setCrop(newCrop);
                  }
                }}
                className={toolChipClass}
              >
                <span>Reset</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 absolute bottom-0 left-1/2 -translate-x-1/2">
          <button
            type="button"
            onClick={onClose}
            className={toolCancelClass}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleApply}
            className={toolApplyClass}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}